import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Table, TableBody, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material'
import Stack from '@mui/material/Stack'
import { useHistory } from 'react-router-dom'
import { singular } from 'pluralize'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder/EmptyPlaceholder'
import LoadingBackdrop from '../../shared/components/LoadingBackdrop/LoadingBackdrop'
import TableColumnFilter from '../../shared/components/TableColumnFilter'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../shared/hooks/useIsSnapshotMode'
import { useScrollHighlight } from '../../shared/hooks/useScrollHighlight'
import { useTableColumnLayout } from '../../shared/hooks/useTableColumnLayout'
import { useTableRowRestore } from '../../shared/hooks/useTableRowRestore'
import CommentsViewAllFilters from './components/CommentsViewAllFilters'
import CommentsViewTabFilters from './components/CommentsViewTabFilters'
import CommentsViewDateRangeFilters from './components/CommentsViewDateRangeFilters'
import { CommentsTableRow } from './components/CommentsTableRow'
import { HeaderCell } from './components/CommentsTableCells'
import { CommentsViewToolbar, ExportButton, ExportFilenameDialog } from './components/CommentsViewToolbar'
import {
    COMMENTS_VIEW_COLUMNS,
    DEFAULT_HIDDEN_COMMENTS_COLUMN_IDS,
    HIDEABLE_COMMENTS_VIEW_COLUMNS,
    downloadCommentsCsv,
    getCommentsCsv,
    getCommentsCsvBaseName,
    getDefaultCommentsCsvFilename,
    getNormalizedCommentsCsvFilename,
    SortState,
    sortCommentRows,
} from './CommentsView.helpers'
import {
    CommentsViewColumnId,
    CommentsViewComment,
    CommentsViewDateRangeFiltersState,
    CommentsViewDomainFilterId,
    CommentsViewDomainTabId,
    CommentsViewNavigationTarget,
    CommentsViewSelectFilterConfig,
    CommentsViewTab,
} from './CommentsView.types'
import { CommentsViewDerivedTabState } from './useCommentsViewDerivedData'
import { CommentsViewFilterHandlers } from './useCommentsViewFilters'

type CommentsTableContentProps = {
    tab: CommentsViewTab
    strategyTitle: string
    viewState: CommentsViewDerivedTabState
    searchQuery: string
    onSearchQueryChange: (value: string) => void
    isLoading: boolean
    isError: boolean
    isCommentSaving: (commentId: string) => boolean
    onUpdateComment: (comment: CommentsViewComment, updates: { action?: string; isResolved?: boolean }) => Promise<void>
    dateRange: CommentsViewDateRangeFiltersState
    filterHandlers: CommentsViewFilterHandlers
    hiddenColumnIds: CommentsViewColumnId[]
    onHiddenColumnIdsChange: (hiddenColumnIds: CommentsViewColumnId[]) => void
}
const CommentsTableContent = memo(
    ({
        tab,
        strategyTitle,
        viewState,
        searchQuery,
        onSearchQueryChange,
        isLoading,
        isError,
        isCommentSaving,
        onUpdateComment,
        dateRange,
        filterHandlers,
        hiddenColumnIds,
        onHiddenColumnIdsChange,
    }: CommentsTableContentProps) => {
        const {
            onCommonFilterChange,
            onDateRangeChange,
            onDomainFilterChange,
            onResetFilters,
            onResetGeneralFilters,
            onResetTabDomainFilters,
        } = filterHandlers
        const history = useHistory()
        const isReadonlyMode = useIsRoadmapReadonly()
        const isSnapshotMode = useIsSnapshotMode()
        const { scheduleRestore, restoreState, clearRestore } = useTableRowRestore<SortState>('commentsView')

        const [sortState, setSortState] = useState<SortState>(restoreState?.sortState ?? null)
        const [page, setPage] = useState(restoreState?.page ?? 0)
        const [rowsPerPage, setRowsPerPage] = useState(restoreState?.rowsPerPage ?? 10)
        const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
        const [exportFilenameDraft, setExportFilenameDraft] = useState('')
        const { visibleColumns, visibleColumnIds, tableMinWidth } = useTableColumnLayout({
            columns: COMMENTS_VIEW_COLUMNS,
            hiddenColumnIds,
            minTableWidth: 560,
        })
        const {
            filteredComments,
            commonFilterEntries,
            domainFilterConfigsForTab,
            allFilterGroups,
            activeDomainTabId,
            hasCommentsForTab,
            hasFilteredComments,
            activeCommonFiltersCount,
            activeDomainFiltersCountForTab,
            hasActiveFilters,
        } = viewState

        const canEditComments = !isSnapshotMode && !isReadonlyMode
        const isEditingDisabled = !canEditComments

        const [highlightCommentId, setHighlightCommentId] = useState<string | null>(restoreState?.commentId ?? null)

        useScrollHighlight({
            highlightId: highlightCommentId,
            onClear: () => setHighlightCommentId(null),
        })

        useEffect(() => {
            if (restoreState) {
                clearRestore()
            }
        }, [restoreState, clearRestore])

        const handleLocationClick = useCallback(
            (target: CommentsViewNavigationTarget, commentId: string) => {
                scheduleRestore({
                    commentId,
                    tabId: tab.id,
                    sortState,
                    page,
                    rowsPerPage,
                    searchQuery,
                })
                history.push(target)
            },
            [history, scheduleRestore, tab.id, sortState, page, rowsPerPage, searchQuery]
        )

        const commonFilterConfigs = useMemo(
            (): CommentsViewSelectFilterConfig[] =>
                commonFilterEntries.map(({ definition, options, selectedOptionIds }) => ({
                    id: definition.id,
                    label: definition.label,
                    placeholder: definition.placeholder,
                    options,
                    selectedOptionIds,
                    isActive: selectedOptionIds.length !== options.length,
                    onChange: (nextSelectedOptionIds) =>
                        onCommonFilterChange(
                            definition.id,
                            nextSelectedOptionIds,
                            options.map((option) => option.id)
                        ),
                    onClear: () =>
                        onCommonFilterChange(
                            definition.id,
                            options.map((option) => option.id),
                            options.map((option) => option.id)
                        ),
                })),
            [commonFilterEntries, onCommonFilterChange]
        )

        const commentRows = useMemo(
            () =>
                filteredComments.map((comment) => ({
                    comment,
                    typeLabel: tab.getTypeLabel(comment.dataType),
                    locationLabel: tab.getLocationLabel(comment),
                    locationTarget: tab.getLocationTarget(comment, history.location.pathname),
                })),
            [filteredComments, history.location.pathname, tab]
        )

        const sortedComments = useMemo(() => sortCommentRows(commentRows, sortState), [sortState, commentRows])

        const isRestoringPaginationRef = useRef(!!restoreState)

        useEffect(() => {
            if (isRestoringPaginationRef.current) {
                if (isLoading) return
                isRestoringPaginationRef.current = false
                return
            }
            setPage(0)
        }, [filteredComments.length, isLoading])

        useEffect(() => {
            if (!restoreState) return
            const maxPage = Math.max(0, Math.ceil(filteredComments.length / rowsPerPage) - 1)
            if (page > maxPage) {
                setPage(maxPage)
            }
        }, [restoreState, filteredComments.length, rowsPerPage, page])

        const paginatedComments = useMemo(
            () => sortedComments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            [sortedComments, page, rowsPerPage]
        )

        const suggestedExportFilename = useMemo(
            () => getDefaultCommentsCsvFilename(strategyTitle, tab.label, hasActiveFilters),
            [hasActiveFilters, strategyTitle, tab.label]
        )
        const suggestedExportBaseName = useMemo(
            () => getCommentsCsvBaseName(suggestedExportFilename),
            [suggestedExportFilename]
        )

        const handleOpenExportDialog = useCallback(() => {
            setExportFilenameDraft(suggestedExportBaseName)
            setIsExportDialogOpen(true)
        }, [suggestedExportBaseName])

        const handleCloseExportDialog = useCallback(() => {
            setIsExportDialogOpen(false)
        }, [])

        const handleConfirmExport = useCallback(() => {
            const csv = getCommentsCsv(
                sortedComments.map(({ comment }) => comment),
                visibleColumnIds,
                (comment) => tab.getTypeLabel(comment.dataType),
                (comment) => tab.getLocationLabel(comment)
            )
            const filename = getNormalizedCommentsCsvFilename(exportFilenameDraft, suggestedExportFilename)

            downloadCommentsCsv(filename, csv)
            setIsExportDialogOpen(false)
        }, [exportFilenameDraft, sortedComments, suggestedExportFilename, tab, visibleColumnIds])

        const handleSort = useCallback((columnId: CommentsViewColumnId) => {
            setSortState((currentState) => {
                if (!currentState || currentState.columnId !== columnId) {
                    return { columnId, direction: 'asc' }
                }

                return {
                    columnId,
                    direction: currentState.direction === 'asc' ? 'desc' : 'asc',
                }
            })
        }, [])

        const handleResetAllFilters = useCallback(() => {
            onResetFilters(tab.id)
            onSearchQueryChange('')
        }, [onResetFilters, tab.id, onSearchQueryChange])

        if (isLoading && !hasCommentsForTab) {
            return <LoadingBackdrop loading title="Loading comments" subtitle="Fetching comments for this tab." />
        }

        if (isError && !hasCommentsForTab) {
            return (
                <EmptyPlaceholder
                    title="Unable to load comments"
                    subtitle="Refresh the page or try again in a moment."
                    sx={{ minHeight: 280 }}
                />
            )
        }

        const filtersControls = (
            <Stack gap={2} rowGap={1} sx={{ borderBottom: '1px solid', borderColor: 'figma.grayscale.stroke', pb: 1 }}>
                <CommentsViewTabFilters
                    title="General Filters"
                    filters={commonFilterConfigs}
                    appliedCount={activeCommonFiltersCount}
                    onClear={activeCommonFiltersCount > 0 ? onResetGeneralFilters : undefined}
                    isCollapsible={true}
                    headerActions={
                        <TableColumnFilter
                            availableColumns={HIDEABLE_COMMENTS_VIEW_COLUMNS}
                            hiddenColumnIds={hiddenColumnIds}
                            defaultHiddenColumnIds={DEFAULT_HIDDEN_COMMENTS_COLUMN_IDS}
                            onHiddenColumnIdsChange={onHiddenColumnIdsChange}
                        />
                    }
                    extraControl={
                        <CommentsViewDateRangeFilters
                            from={dateRange.from}
                            to={dateRange.to}
                            onFromChange={(value) => onDateRangeChange('from', value)}
                            onToChange={(value) => onDateRangeChange('to', value)}
                        />
                    }
                />
                {tab.id === 'all' ? (
                    allFilterGroups.length ? (
                        <CommentsViewAllFilters
                            groups={allFilterGroups}
                            onGroupReset={onResetTabDomainFilters}
                            onSelectionChange={onDomainFilterChange}
                        />
                    ) : null
                ) : domainFilterConfigsForTab.length ? (
                    <CommentsViewTabFilters
                        title={singular(tab.label) + ' Filters'}
                        filters={domainFilterConfigsForTab.map((filter) => ({
                            ...filter,
                            onChange: (nextSelectedOptionIds) =>
                                onDomainFilterChange(
                                    activeDomainTabId as CommentsViewDomainTabId,
                                    filter.id as CommentsViewDomainFilterId,
                                    nextSelectedOptionIds,
                                    filter.options.map((option) => option.id)
                                ),
                            onClear: () =>
                                onDomainFilterChange(
                                    activeDomainTabId as CommentsViewDomainTabId,
                                    filter.id as CommentsViewDomainFilterId,
                                    filter.options.map((option) => option.id),
                                    filter.options.map((option) => option.id)
                                ),
                        }))}
                        appliedCount={activeDomainFiltersCountForTab}
                        onClear={
                            activeDomainFiltersCountForTab > 0 && activeDomainTabId
                                ? () => onResetTabDomainFilters(activeDomainTabId)
                                : undefined
                        }
                        defaultExpanded
                    />
                ) : null}
            </Stack>
        )

        if (!hasCommentsForTab) {
            return (
                <Stack gap={3}>
                    <CommentsViewToolbar
                        searchQuery={searchQuery}
                        onSearchQueryChange={onSearchQueryChange}
                        isResetDisabled
                        onReset={handleResetAllFilters}
                        exportSlot={<ExportButton onClick={handleOpenExportDialog} disabled />}
                    />
                    <EmptyPlaceholder
                        title={tab.emptyStateTitle}
                        subtitle={tab.emptyStateSubtitle}
                        sx={{ minHeight: 280 }}
                    />
                    <ExportFilenameDialog
                        open={isExportDialogOpen}
                        value={exportFilenameDraft}
                        placeholder={suggestedExportBaseName}
                        onChange={setExportFilenameDraft}
                        onClose={handleCloseExportDialog}
                        onConfirm={handleConfirmExport}
                    />
                </Stack>
            )
        }

        const isResetDisabled = !hasActiveFilters && !searchQuery

        return (
            <Stack gap={2}>
                <CommentsViewToolbar
                    searchQuery={searchQuery}
                    onSearchQueryChange={onSearchQueryChange}
                    isResetDisabled={isResetDisabled}
                    onReset={handleResetAllFilters}
                    exportSlot={<ExportButton onClick={handleOpenExportDialog} />}
                />
                {filtersControls}

                {!hasFilteredComments ? (
                    <EmptyPlaceholder
                        title="No comments match current filters"
                        subtitle="Adjust filters or search to broaden the results."
                        sx={{ minHeight: 280 }}
                    />
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                overflowX: 'auto',
                                width: '100%',
                                maxWidth: '100%',
                                '.MuiTableCell-root': {
                                    borderBottom: 'none',
                                    verticalAlign: 'middle',
                                },
                                '.MuiTableBody-root .MuiTableRow-root:not(:last-child) .MuiTableCell-root': {
                                    borderBottom: '1px solid',
                                    borderColor: 'figma.grayscale.stroke',
                                },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    minWidth: tableMinWidth,
                                    width: '100%',
                                    tableLayout: 'fixed',
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {visibleColumns.map((column) => (
                                            <HeaderCell
                                                key={column.id}
                                                width={column.width}
                                                isActive={sortState?.columnId === column.id}
                                                direction={
                                                    sortState?.columnId === column.id ? sortState.direction : 'asc'
                                                }
                                                onClick={() => handleSort(column.id)}
                                            >
                                                {column.label}
                                            </HeaderCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedComments.map(
                                        ({ comment, typeLabel, locationLabel, locationTarget }, commentIndex) => (
                                            <CommentsTableRow
                                                key={comment._id}
                                                comment={comment}
                                                commentIndex={commentIndex}
                                                commentsLength={paginatedComments.length}
                                                displayNumber={page * rowsPerPage + commentIndex + 1}
                                                visibleColumnIds={visibleColumnIds}
                                                typeLabel={typeLabel}
                                                locationLabel={locationLabel}
                                                locationTarget={locationTarget as CommentsViewNavigationTarget | null}
                                                isHighlighted={highlightCommentId === comment._id}
                                                isEditingDisabled={isEditingDisabled}
                                                isSaving={isCommentSaving(comment._id)}
                                                onLocationClick={handleLocationClick}
                                                onResolvedChange={(currentComment, isResolved) =>
                                                    onUpdateComment(currentComment, { isResolved })
                                                }
                                                onActionSave={(currentComment, action) =>
                                                    onUpdateComment(currentComment, { action })
                                                }
                                            />
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={sortedComments.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(+event.target.value)
                                setPage(0)
                            }}
                            sx={{
                                '.MuiTablePagination-spacer': { display: 'none' },
                                '.MuiTablePagination-toolbar': { pl: 0 },
                            }}
                        />
                    </>
                )}
                <ExportFilenameDialog
                    open={isExportDialogOpen}
                    value={exportFilenameDraft}
                    placeholder={suggestedExportBaseName}
                    onChange={setExportFilenameDraft}
                    onClose={handleCloseExportDialog}
                    onConfirm={handleConfirmExport}
                />
            </Stack>
        )
    }
)

export default CommentsTableContent
